import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeft,
  Plane,
  ExternalLink,
  User,
  Building2,
  CheckCircle2,
  RotateCcw,
  Trash2,
  X,
  AlertCircle
} from 'lucide-react';
import kadryService from '../services/kadryService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('pl-PL');
  } catch {
    return '—';
  }
};

/** Zgodnie z backendem: sprawdzenie merytoryczne rachunku przez przełożonego */
const czyMerytoryczniePodpisany = (w) => {
  const p = w?.sprawdzenieMerytoryczne?.podpis;
  return !!(p && String(p).trim() !== '');
};

const czyEtapPoczatkowy = (w) => {
  if (!w || w.akceptacjaFormalnaZakonczona) return false;
  const maPodpis = !!(w.podpisPrzełożonego && String(w.podpisPrzełożonego).trim() !== '');
  return !maPodpis && !w.rachunekKosztowUzupełniony;
};

const czyPrzedRachunkiem = (w) => {
  if (!w || w.akceptacjaFormalnaZakonczona) return false;
  const maPodpis = !!(w.podpisPrzełożonego && String(w.podpisPrzełożonego).trim() !== '');
  return maPodpis && !w.rachunekKosztowUzupełniony && !czyMerytoryczniePodpisany(w);
};

const refId = (ref) => {
  if (!ref) return '';
  return String(ref._id || ref);
};

const userIdStr = (user) => String(user?.id || user?._id || '');

const jestAutoremWniosku = (w, user) => refId(w.createdBy) === userIdStr(user);
const jestAdresatemWniosku = (w, user) => refId(w.przełożony) === userIdStr(user);

const mozeUsunacWniosek = (w, user) => {
  if (!w || w.akceptacjaFormalnaZakonczona) return false;
  if (!userIdStr(user)) return false;
  const jestAutorem = jestAutoremWniosku(w, user);
  const jestAdresatem = jestAdresatemWniosku(w, user);
  const jestAdminem = user?.role === 'admin' || user?.role === 'super admin';
  const jestHrKs = user?.department === 'HR' || user?.department === 'KSIĘGOWOŚĆ';

  if (w.oczekujeNaPoprawkePolecenia && jestAutorem) return true;
  if (czyEtapPoczatkowy(w)) {
    return jestAutorem || jestAdresatem || jestAdminem || jestHrKs;
  }
  if (czyPrzedRachunkiem(w)) {
    return jestAutorem || jestAdresatem || jestAdminem;
  }
  return false;
};

const mozeZwrocPolecenie = (w, user) => {
  if (!czyEtapPoczatkowy(w) || w.oczekujeNaPoprawkePolecenia) return false;
  const jestAdresatem = jestAdresatemWniosku(w, user);
  const jestAdminem = user?.role === 'admin' || user?.role === 'super admin';
  return jestAdresatem || jestAdminem;
};

/** Query do wniosku: scroll + ramka na polu podpisu (WniosekDelegacyjny.js); użyte w Link poniżej. */
// eslint-disable-next-line no-unused-vars -- false positive: wywołanie w template `to={...}` poniżej
const queryFocusDelegacja = (w) => {
  if (w.akceptacjaFormalnaZakonczona) return '';
  const meryt = czyMerytoryczniePodpisany(w);
  if (
    w.widokWniosku === 'adresat' &&
    w.rachunekKosztowUzupełniony &&
    w.podpisPrzełożonego &&
    !meryt
  ) {
    return '?focus=podpis-merytoryczny';
  }
  if (w.widokWniosku === 'hr_ks' && !w.akceptacjaFormalnaZakonczona) {
    return '?focus=podpis-formalny';
  }
  return '';
};

/** Etykieta statusu dla listy wniosków delegacyjnych */
const statusDelegacji = (w) => {
  if (w.oczekujeNaPoprawkePolecenia) {
    return {
      label: w.widokWniosku === 'autor'
        ? 'Do poprawy polecenia (uwagi przełożonego)'
        : 'U pracownika — oczekuje na poprawę polecenia',
      cls: 'bg-orange-100 text-orange-950 border border-orange-300'
    };
  }
  if (w.oczekujeNaPoprawkeRachunku) {
    return {
      label: 'Do poprawy rachunku (uwagi HR / Księgowość)',
      cls: 'bg-orange-100 text-orange-950 border border-orange-300'
    };
  }
  if (w.akceptacjaFormalnaZakonczona) {
    return {
      label: 'Zaakceptowany — zamknięty',
      cls: 'bg-violet-100 text-violet-900 border border-violet-300',
      ukonczony: true
    };
  }
  const adresat = w.widokWniosku === 'adresat';
  const autor = w.widokWniosku === 'autor';
  const hrKs = w.widokWniosku === 'hr_ks';
  const merytorycznieOk = czyMerytoryczniePodpisany(w);
  if (!w.podpisPrzełożonego) {
    return adresat
      ? { label: 'Do Twojego podpisu', cls: 'bg-amber-100 text-amber-900' }
      : { label: 'Oczekuje na podpis przełożonego', cls: 'bg-slate-100 text-slate-800' };
  }
  if (w.rachunekKosztowUzupełniony) {
    if (hrKs) {
      return {
        label: 'Do akceptacji formalnej (HR / Księgowość)',
        cls: 'bg-rose-50 text-rose-900 border border-rose-200'
      };
    }
    if (!merytorycznieOk) {
      if (adresat) {
        return {
          label: 'Do Twojego podpisu — sprawdzenie merytoryczne rachunku',
          cls: 'bg-amber-100 text-amber-900 border border-amber-300'
        };
      }
      if (autor) {
        return {
          label: 'Oczekuje na sprawdzenie merytoryczne (przełożony)',
          cls: 'bg-slate-100 text-slate-800'
        };
      }
    }
    if (adresat && merytorycznieOk) {
      return {
        label: 'Oczekuje na akceptację formalną (HR / Księgowość)',
        cls: 'bg-slate-100 text-slate-800'
      };
    }
    return { label: 'Rachunek uzupełniony', cls: 'bg-emerald-100 text-emerald-900' };
  }
  if (w.oczekujeNaRachunekKosztow) {
    return { label: 'Uzupełnij rachunek (po powrocie)', cls: 'bg-amber-100 text-amber-950' };
  }
  return { label: 'Polecenie podpisane', cls: 'bg-blue-100 text-blue-900' };
};

const WnioskiDelegacyjneLista = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showZwrotModal, setShowZwrotModal] = useState(false);
  const [komentarzZwrotu, setKomentarzZwrotu] = useState('');
  const [wniosekDoAkcji, setWniosekDoAkcji] = useState(null);

  const jestHrKs =
    user?.department === 'HR' || user?.department === 'KSIĘGOWOŚĆ';
  const pokazOpisKolejkiFormalnej =
    jestHrKs || user?.role === 'super admin';
  const { data, isLoading, isError, refetch } = useQuery(
    'wnioski-delegacyjne',
    () => kadryService.getWnioskiDelegacyjne(),
    { refetchInterval: 60 * 1000 }
  );

  const zwrotMutation = useMutation(
    ({ id, komentarz }) => kadryService.zwrocPolecenieDelegacjiDoPracownika(id, komentarz),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        toast.success('Wniosek zwrócony do pracownika do poprawy');
        setShowZwrotModal(false);
        setWniosekDoAkcji(null);
        setKomentarzZwrotu('');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Nie udało się zwrócić wniosku');
      }
    }
  );

  const usunMutation = useMutation(
    (id) => kadryService.usunDelegacje(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-delegacyjne');
        toast.success('Wniosek delegacyjny został usunięty');
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Nie udało się usunąć wniosku');
      }
    }
  );

  const wnioski = data?.wnioski || [];

  const handleUsun = (w) => {
    if (!window.confirm(`Czy na pewno usunąć wniosek delegacyjny ${w.pracownikImie} ${w.pracownikNazwisko} (${fmtDate(w.dataOd)} – ${fmtDate(w.dataDo)})? Tej operacji nie można cofnąć.`)) {
      return;
    }
    usunMutation.mutate(w._id);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <Link
              to="/kadry/dokumenty"
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Powrót do dokumentów"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Link>
            <p className="text-sm text-gray-500">
              {pokazOpisKolejkiFormalnej
                ? 'Twoje wnioski i wnioski jako przełożony. W kolejce HR / Księgowość widzisz tylko wnioski, w których pracownik potwierdził rachunek i przełożony zapisał sprawdzenie merytoryczne — wtedy wysyłane są powiadomienia do HR i Księgowości.'
                : 'Twoje złożone wnioski oraz wnioski skierowane do Ciebie jako przełożonego. Przed uzupełnieniem rachunku możesz usunąć duplikat; przed podpisem polecenia — zwrócić wniosek do poprawy.'}
            </p>
          </div>
          <Link
            to="/kadry/dokumenty/wniosek-delegacyjny"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
          >
            <Plane className="h-4 w-4" />
            Nowy wniosek
          </Link>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        )}
        {isError && (
          <div className="p-8 text-center text-red-600">
            Nie udało się wczytać listy.{' '}
            <button type="button" className="underline font-medium" onClick={() => refetch()}>
              Spróbuj ponownie
            </button>
          </div>
        )}
        {!isLoading && !isError && wnioski.length === 0 && (
          <p className="p-8 text-center text-gray-500">Brak wniosków delegacyjnych.</p>
        )}
        {!isLoading && !isError && wnioski.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-700">Data złożenia</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Rola</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Pracownik</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Miejsce</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Termin</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700 min-w-[200px]">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {wnioski.map((w) => {
                  const st = statusDelegacji(w);
                  const pokazUsun = mozeUsunacWniosek(w, user);
                  const pokazZwrot = mozeZwrocPolecenie(w, user);
                  return (
                  <tr
                    key={w._id}
                    className={`border-b border-gray-100 hover:bg-gray-50/80 ${
                      w.akceptacjaFormalnaZakonczona ? 'bg-violet-50/50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(w.createdAt || w.dataZlozenia)}</td>
                    <td className="px-4 py-3">
                      {w.widokWniosku === 'adresat' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 text-xs font-medium px-2.5 py-0.5">
                          <User className="h-3 w-3" />
                          Adresat
                        </span>
                      ) : w.widokWniosku === 'hr_ks' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-900 border border-rose-200 text-xs font-medium px-2.5 py-0.5">
                          <Building2 className="h-3 w-3" />
                          HR / Księgowość
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-800 text-xs font-medium px-2.5 py-0.5">
                          Autor
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {w.pracownikImie} {w.pracownikNazwisko}
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate" title={w.miejsceDelegacji || ''}>
                      {w.miejsceDelegacji || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {fmtDate(w.dataOd)} – {fmtDate(w.dataDo)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full text-xs font-medium px-2.5 py-0.5 ${st.cls}`}
                      >
                        {w.akceptacjaFormalnaZakonczona && (
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                        )}
                        {w.oczekujeNaPoprawkePolecenia && (
                          <AlertCircle className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                        )}
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Link
                          to={`/kadry/dokumenty/wniosek-delegacyjny/${w._id}${queryFocusDelegacja(w)}`}
                          className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-medium whitespace-nowrap"
                        >
                          Otwórz
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        {pokazZwrot && (
                          <button
                            type="button"
                            onClick={() => { setWniosekDoAkcji(w); setShowZwrotModal(true); setKomentarzZwrotu(''); }}
                            className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900 font-medium whitespace-nowrap"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Zwróć do poprawy
                          </button>
                        )}
                        {pokazUsun && (
                          <button
                            type="button"
                            onClick={() => handleUsun(w)}
                            disabled={usunMutation.isLoading}
                            className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 font-medium whitespace-nowrap disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Usuń
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showZwrotModal && wniosekDoAkcji && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Zwróć wniosek do poprawy</h3>
              <button
                type="button"
                onClick={() => { setShowZwrotModal(false); setWniosekDoAkcji(null); setKomentarzZwrotu(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Wniosek <strong>{wniosekDoAkcji.pracownikImie} {wniosekDoAkcji.pracownikNazwisko}</strong> wróci do pracownika z Twoimi uwagami. Po poprawie trafi ponownie do Ciebie do podpisania polecenia wyjazdu.
            </p>
            <label className="block text-sm font-medium mb-2">Komentarz dla pracownika *</label>
            <textarea
              value={komentarzZwrotu}
              onChange={(e) => setKomentarzZwrotu(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="Opisz, co pracownik powinien poprawić..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="mt-1 text-xs text-gray-500 text-right">{komentarzZwrotu.length}/2000</p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => { setShowZwrotModal(false); setWniosekDoAkcji(null); setKomentarzZwrotu(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={() => zwrotMutation.mutate({ id: wniosekDoAkcji._id, komentarz: komentarzZwrotu.trim() })}
                disabled={!komentarzZwrotu.trim() || zwrotMutation.isLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {zwrotMutation.isLoading ? <LoadingSpinner size="sm" /> : <RotateCcw className="h-4 w-4" />}
                Zwróć do pracownika
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WnioskiDelegacyjneLista;
